<template>
  <article class="vacancy-show">
    <PageSection>
      <VacancyHeader
        :vacancy="vacancy"
        :employer-data="employerData"
        :company="company"
      />
    </PageSection>

    <PageSection
      v-if="vacancyResult && vacancyResult.displayForm && !updatedVacancyResult"
    >
      <SectionBox>
        <BasicSection>
          <SectionTitle title="Результат поиска" divider />
          <VacancyResultForm
            :vacancy-id="vacancy.id"
            :vacancy-result="vacancyResult"
            @success="onVacancyResultSuccess"
          />
        </BasicSection>
      </SectionBox>
    </PageSection>

    <PageSection v-if="resultMessage">
      <SectionBox>
        <BasicSection>
          <SectionTitle title="Результат поиска" divider />
          <p v-html="resultMessage" />
        </BasicSection>
      </SectionBox>
    </PageSection>

    <PageSection>
      <VacancyDescription
        :vacancy="vacancy"
        :default-expanded="!employerData"
        :show-favorite="!!createResponse"
      />
    </PageSection>

    <PageSection v-if="employerData">
      <SectionBox>
        <BasicSection>
          <SectionTitle title="Потенциальные кандидаты" divider />
          <BasicColumns :columns="2" appearance="suitable-users">
            <div>
              <SectionSubtitle :title="suitableSpecialistsCount" />
              <p v-html="suitableSpecialistsDescription" />
              <BasicButton
                size="m"
                target="_blank"
                :href="`/vacancies/${vacancy.id}/suitable_users`"
                appearance="primary"
              >
                Все специалисты
              </BasicButton>
            </div>
            <div>
              <SectionSubtitle :title="vacancyGuestsCount" />
              <p v-html="vacancyGuestsDescription" />
              <BasicButton
                size="m"
                :href="`/vacancies/${vacancy.id}/guests`"
                appearance="primary"
              >
                Все гости
              </BasicButton>
            </div>
          </BasicColumns>
        </BasicSection>
      </SectionBox>
    </PageSection>
    <PageSection v-if="transformedTabs">
      <SectionBox>
        <BasicSection appearance="responses">
          <SectionTitle title="Отклики на вакансию" />
        </BasicSection>
        <BasicTabs
          v-if="transformedTabs"
          :tabs="transformedTabs"
          :active-tab-name="activeTabName"
          @update:activeTabName="updateActiveTab"
        />
        <BasicSection
          :key="activeTabName"
          appearance="always-same-paddings"
        >
          <NoContent
            v-if="getActiveTab.count === 0"
            :title="getNoContent.title"
            :description="getNoContent.description"
            :icon="getNoContent.icon"
          />
          <VacancyResponses
            v-if="getActiveTab.count > 0"
            :vacancy-id="vacancy.id"
            :group="activeTabName"
            :company-id="companyId"
            @changeFavouritesCount="updateFavouritesTabsCount"
            @changeArchivedCount="updateArchivedTabsCount"
          />
        </BasicSection>
      </SectionBox>
    </PageSection>
  </article>
</template>

<script>
import { notify } from 'src/notify.js';
import { getRussianPluralDeclension } from 'src/utils/translation.js';

import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicColumns from 'src/components/BasicColumns/BasicColumns.vue';
import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
import SectionBox from 'src/components/SectionBox/SectionBox.vue';
import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
import PageSection from 'src/components/sections/PageSection/PageSection.vue';
import SectionSubtitle from 'src/components/sections/SectionSubtitle/SectionSubtitle.vue';
import SectionTitle from 'src/components/sections/SectionTitle/SectionTitle.vue';
import BasicTabs from 'src/components/tabs/BasicTabs/BasicTabs.vue';
import VacancyDescription from 'src/components/vacancies/VacancyDescription/VacancyDescription.vue';
import VacancyHeader from 'src/components/vacancies/VacancyHeader/VacancyHeader.vue';
import VacancyResponses from 'src/components/vacancies/VacancyResponses/VacancyResponses.vue';
import VacancyResultForm from 'src/components/vacancies/VacancyResultForm/VacancyResultForm.vue';

import icon from 'images/no_content.icon.responses.new.svg';

const suitableSpecialists = getRussianPluralDeclension(
  '${0} подходящий специалист',
  '${0} подходящих специалиста',
  '${0} подходящих специалистов',
);

const vacancyGuests = getRussianPluralDeclension(
  '${0} гость вакансии',
  '${0} гостя вакансии',
  '${0} гостей вакансии',
);

const improveVacancyLink = '/info/help_hr#q2.15';

export default {
  name: 'VacancyPage',
  components: {
    BasicTabs,
    BasicColumns,
    BasicButton,
    NoContent,
    PageSection,
    VacancyHeader,
    SectionSubtitle,
    VacancyDescription,
    SectionBox,
    BasicSection,
    SectionTitle,
    VacancyResponses,
    VacancyResultForm,
  },

  noContent: {
    vacancy_responses_favorited_count: {
      title: 'Нет избранных откликов',
      description:
        'Добавляйте в избранное подходящие отклики, чтобы быстро к ним вернуться.',
      icon: {
        src: icon,
        width: '96px',
        height: '96px',
      },
    },
    vacancy_responses_archived_count: {
      title: 'Нет откликов с отказом',
      description:
        'Откажите неподходящим кандидатам, чтобы их отклики не мешали отслеживать остальные.',
      icon: {
        src: icon,
        width: '96px',
        height: '96px',
      },
    },
    vacancy_responses_all_count: {
      title: 'Нет откликов',
      description: 'На вакансию пока никто не откликнулся.',
      icon: {
        src: icon,
        width: '96px',
        height: '96px',
      },
    },
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    placeholder: {
      type: Object,
      required: false,
    },
    responses: {
      type: Object,
      required: false,
    },
    createResponse: {
      type: Object,
      required: false,
    },
    employerData: {
      type: Object,
      required: false,
    },
    vacancyResult: {
      type: Object,
      required: false,
    },
    company: {
      type: Object,
      required: false,
    },
  },

  data() {
    const activeTab = this.responses
      ? this.responses.tabs.find((tab) => tab.active)
      : {};
    return {
      tabs: this.responses ? this.responses.tabs : null,
      activeTabName: activeTab.name,
      updatedVacancyResult: null,
    };
  },

  vacancyResultMessages: {
    no_result_persistant: `Если вам не удалось никого найти — попробуйте доработать вакансию и разместить её ещё раз. Воспользуйтесь нашими советами о том, <a href="${improveVacancyLink}" target="_blank">как улучшить вакансию</a>.`,
    no_result: `Спасибо за ответ! Если вам не удалось никого найти — попробуйте доработать вакансию и разместить её ещё раз. Воспользуйтесь нашими советами о том, <a href="${improveVacancyLink}" target="_blank">как улучшить вакансию</a>.`,
    found_here: 'Спасибо за ответ! Мы рады, что помогли вам усилить команду.',
    found_elsewhere:
      'Спасибо за ответ! Надеемся, что в следующий раз специалист найдется именно у нас.',
    stop_found:
      'Спасибо за ответ! Будем рады снова видеть ваши вакансии на Хабр Карьере, когда поиск возобновится.',
  },

  methods: {
    onVacancyResultSuccess(e) {
      this.updatedVacancyResult = e;
      notify({ message: 'Данные сохранены' });
    },

    updateActiveTab(tabName) {
      this.activeTabName = tabName;
    },

    updateArchivedTabsCount(incrementValue) {
      this.tabs = this.tabs.map((tab) => {
        if (tab.name === 'vacancy_responses_all_count') {
          return { ...tab, count: tab.count - incrementValue };
        }

        if (tab.name === 'vacancy_responses_archived_count') {
          return { ...tab, count: tab.count + incrementValue };
        }

        return tab;
      });
    },

    updateFavouritesTabsCount(incrementValue) {
      this.tabs = this.tabs.map((tab) => {
        if (tab.name === 'vacancy_responses_favorited_count') {
          return { ...tab, count: tab.count + incrementValue };
        }

        return tab;
      });
    },
  },

  computed: {
    companyId() {
      return this.vacancy.company.alias_name;
    },
    resultMessage() {
      if (this.vacancyResult && this.vacancyResult.improvementTips) {
        return this.$options.vacancyResultMessages.no_result_persistant;
      }
      if (this.updatedVacancyResult) {
        return this.$options.vacancyResultMessages[this.updatedVacancyResult];
      }
      return null;
    },
    getNoContent() {
      return this.$options.noContent[this.activeTabName];
    },
    transformedTabs() {
      if (!this.tabs) return null;

      return this.tabs.map((tab) => ({
        name: tab.name,
        title: tab.title,
        count: tab.count,
      }));
    },
    getActiveTab() {
      if (!this.tabs) {
        return null;
      }

      const activeTab = this.tabs.find(
        (tab) => tab.name === this.activeTabName,
      );
      return activeTab;
    },
    vacancyGuestsDescription() {
      return 'Мы&nbsp;сохранили профили соискателей, посетивших страницу вашей вакансии. Пишите им&nbsp;личные сообщения с&nbsp;приглашением в&nbsp;вашу вакансию.';
    },
    suitableSpecialistsDescription() {
      if (this.vacancy.locations) {
        return `Мы&nbsp;отобрали наиболее подходящих по&nbsp;навыкам соискателей для&nbsp;вашей вакансии в&nbsp;городе ${this.vacancy.locations[0].title}. Свяжитесь с&nbsp;ними и&nbsp;предложите откликнуться на&nbsp;вашу вакансию.`;
      }
      return 'Мы&nbsp;отобрали наиболее подходящих по&nbsp;навыкам соискателей для&nbsp;вашей вакансии. Свяжитесь с&nbsp;ними и&nbsp;предложите откликнуться на&nbsp;вашу вакансию.';
    },
    suitableSpecialistsCount() {
      const { suitableUsers } = this.employerData || {};
      if (suitableUsers) {
        return suitableSpecialists(suitableUsers.count).replace(
          '${0}',
          suitableUsers.count,
        );
      }
      return null;
    },
    vacancyGuestsCount() {
      const { guestUsers } = this.employerData || {};
      if (guestUsers) {
        return vacancyGuests(guestUsers.count).replace(
          '${0}',
          guestUsers.count,
        );
      }
      return null;
    },
  },
};
</script>
